import { useEffect, useState } from 'react';
import { Check, Copy } from 'lucide-react';

// A command or file for the reader to copy. Plain <pre>: no highlighter, the
// commands are short and the paper palette carries them. The copy button
// only appears to do anything where the Clipboard API exists (it is inert
// in the build-time prerender and on very old WebViews).
export function CodeBlock({ code, label }) {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return undefined;
    const timer = setTimeout(() => setCopied(false), 1800);
    return () => clearTimeout(timer);
  }, [copied]);

  const copy = () => {
    if (typeof navigator === 'undefined' || !navigator.clipboard) return;
    navigator.clipboard.writeText(code).then(() => setCopied(true), () => {});
  };

  return (
    <div className="my-3">
      {label && (
        <div className="text-xs uppercase tracking-wide text-[var(--color-ink-subtle)] mb-1 font-sans">{label}</div>
      )}
      <div className="relative">
        <pre className="code-block font-mono text-sm leading-relaxed bg-[var(--color-surface-input)] border border-[var(--color-divider)] rounded-xl px-4 py-3 pr-14 overflow-x-auto text-[var(--color-ink-main)]">
          <code>{code}</code>
        </pre>
        <button
          type="button"
          onClick={copy}
          aria-label={copied ? 'Copied' : 'Copy to clipboard'}
          className="absolute top-2 right-2 p-2 rounded-lg text-[var(--color-ink-light)] hover:text-[var(--color-ink-main)] hover:bg-[var(--color-hover-fill)] transition-colors"
        >
          {copied
            ? <Check className="w-4 h-4 text-[var(--color-accent-green)]" aria-hidden="true" />
            : <Copy className="w-4 h-4" aria-hidden="true" />}
        </button>
      </div>
    </div>
  );
}

// Inline code: a file name, a setting, a package name. Allowed to wrap and
// to break inside a long token, so a command never runs off a narrow column.
export function Code({ children }) {
  return (
    <code className="font-mono text-[0.9em] px-1.5 py-0.5 rounded bg-[var(--color-surface-input)] text-[var(--color-ink-main)] break-words [overflow-wrap:anywhere]">
      {children}
    </code>
  );
}
